import { UserInfo } from "../models/Interfaces";

export enum Key {
  EncryptionKey = "EncryptionKey_V1_AES-GCM_256",
  EncryptionSaltKey = "EncryptionSalt_V1",
  // Still use "UserAccount" which was the legacy key name for all user info including tokens.
  UserInfo = "UserAccount",
  Tokens = "tokens",
  Login = "persist:login",
}

export interface SecureStorage {
  updateCryptoKey(u: UserInfo, password: string): Promise<void>;
  clear(): Promise<void>;
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<T>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
  unlock(password: string): Promise<boolean>;
}

export enum Platform {
  Browser = "Browser",
  iOS = "iOS",
  Android = "Android",
}

export enum EncryptionVersion {
  AES_GCM_256 = "AES-GCM_256",
}

export type EncryptedPayload = {
  payload: ArrayBuffer;
  iv: Uint8Array;
  // Version exists to mark what type of encryption we used for this payload.
  // This lets us upgrade in the future.
  version: EncryptionVersion;
  // Specifies if this should be in iOS, Android, or a browser
  platform: Platform;
};

export class NoEncryptionKeyError extends Error {
  constructor(msg?: string) {
    super(
      `${msg ? msg + " " : ""}There is no client encryption key. Please set a user account first.`
    );
  }
}

/**
 * Marker prefix for PDF data URLs that should receive special storage treatment on native platforms.
 */
export const PDF_DATA_URL_PREFIX = "data:application/pdf;base64,";

/**
 * Metadata stored in SecureStorage for PDFs that are file-backed.
 * The actual encrypted PDF content is stored in the filesystem.
 */
export interface FileBackedPdfMetadata {
  /** Marker to identify this as file-backed PDF metadata */
  __type: "FileBackedPdf";
  /** Base64-encoded AES-GCM encryption key */
  key: string;
  /** Base64-encoded initialization vector */
  iv: string;
  /** Path to the encrypted file in CACHE directory */
  filePath: string;
  /** Version for future migration support */
  version: 1;
}

/**
 * Type guard to check if a value is FileBackedPdfMetadata
 */
export function isFileBackedPdfMetadata(value: unknown): value is FileBackedPdfMetadata {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as FileBackedPdfMetadata).__type === "FileBackedPdf" &&
    typeof (value as FileBackedPdfMetadata).key === "string" &&
    typeof (value as FileBackedPdfMetadata).iv === "string" &&
    typeof (value as FileBackedPdfMetadata).filePath === "string"
  );
}
